import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ArrowRight, Search, FileText, Map, Rocket } from 'lucide-react';
import SEO from '../components/SEO';

const NotFoundPage = () => {
    return (
        <div className="min-h-screen bg-[#08090c] text-white flex items-center justify-center px-4 relative overflow-hidden">
            <SEO
                title="404 - Page Not Found | Rastor AI"
                description="The page you're looking for doesn't exist. Let us help you find your way to the right career path."
            />

            {/* Background */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[150px]" />
                <div className="absolute bottom-1/4 right-1/3 w-72 h-72 bg-teal-500/8 rounded-full blur-[120px]" />
            </div>

            <div className="relative z-10 max-w-2xl mx-auto text-center">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    {/* 404 Number */}
                    <div className="mb-8">
                        <div className="text-[150px] sm:text-[200px] font-bold leading-none text-transparent bg-clip-text bg-gradient-to-b from-white/20 to-white/5 font-mono select-none">
                            404
                        </div>
                    </div>

                    {/* Message */}
                    <h1 className="text-3xl sm:text-4xl font-bold mb-4">
                        Oops! This path doesn't exist... <span className="text-emerald-400">yet</span>
                    </h1>
                    <p className="text-slate-400 text-lg mb-10 max-w-md mx-auto">
                        Looks like you've wandered off the roadmap. Don't worry — we'll help you find the right direction.
                    </p>

                    {/* Quick links */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 max-w-lg mx-auto">
                        <Link
                            to="/"
                            className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-2xl hover:border-emerald-500/30 hover:bg-white/[0.08] transition-all group"
                        >
                            <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                                <Home className="w-5 h-5 text-emerald-400" />
                            </div>
                            <div className="text-left">
                                <div className="text-sm font-semibold text-white group-hover:text-emerald-400 transition-colors">Home</div>
                                <div className="text-xs text-slate-500">Back to safety</div>
                            </div>
                        </Link>
                        <Link
                            to="/build-roadmap"
                            className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-2xl hover:border-blue-500/30 hover:bg-white/[0.08] transition-all group"
                        >
                            <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center">
                                <Rocket className="w-5 h-5 text-blue-400" />
                            </div>
                            <div className="text-left">
                                <div className="text-sm font-semibold text-white group-hover:text-blue-400 transition-colors">Get Started</div>
                                <div className="text-xs text-slate-500">Begin your journey</div>
                            </div>
                        </Link>
                        <Link
                            to="/careers"
                            className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-2xl hover:border-emerald-500/30 hover:bg-white/[0.08] transition-all group"
                        >
                            <div className="w-10 h-10 bg-violet-500/10 rounded-xl flex items-center justify-center">
                                <Map className="w-5 h-5 text-violet-400" />
                            </div>
                            <div className="text-left">
                                <div className="text-sm font-semibold text-white group-hover:text-violet-400 transition-colors">Explore Careers</div>
                                <div className="text-xs text-slate-500">Browse career paths</div>
                            </div>
                        </Link>
                        <Link
                            to="/resume-builder"
                            className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-2xl hover:border-emerald-500/30 hover:bg-white/[0.08] transition-all group"
                        >
                            <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center">
                                <FileText className="w-5 h-5 text-amber-400" />
                            </div>
                            <div className="text-left">
                                <div className="text-sm font-semibold text-white group-hover:text-amber-400 transition-colors">Resume Builder</div>
                                <div className="text-xs text-slate-500">Build your resume</div>
                            </div>
                        </Link>
                    </div>

                    {/* Main CTA */}
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-2xl shadow-xl shadow-emerald-500/30"
                        >
                            <span>Take Me Home</span>
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export default NotFoundPage;
